import { AuthTokenResponse } from '../models/dto/authTokenResponse';
import axios, {
	AxiosError,
	AxiosRequestConfig,
	RawAxiosRequestHeaders
} from 'axios';
import { UserContextSummary } from '../models/dto/userContext';
import { setAuthHeader } from './apiHelpers';
import { MagicLinkRequest } from '../models/dto/magicLinkRequest';

class AuthProvider {
	private readonly authKey = 'tethr_auth';
	/**
	 * allows for discrepancies between the server time
	 * and local machine time. 5 minutes comes from here:
	 * https://docs.microsoft.com/en-us/windows/security/threat-protection/security-policy-settings/maximum-tolerance-for-computer-clock-synchronization
	 */
	private readonly clockDiscrepancyBufferMs = 5 * 60 * 1000;
	private session: Session | null = null;
	private lastEmail: string | null = null;
	private authChangedCbs: AuthChangedCb[] = [];
	private userContextChangedCbs: UserContextChangedCb[] = [];
	private lastEmailChangedCbs: LastEmailChangedCb[] = [];
	private initialized = false;
	
	constructor() {}
	
	initialize(): void {
		if (this.initialized) return;
		
		this.initialized = true;
		
		// initialize auth data
		try {
			const auth = localStorage.getItem(this.authKey);
			if (auth) {
				const parsed: Auth = JSON.parse(auth);
				if (parsed.token && !this.isTimestampStale(parsed.expireAt)) {
					this.updateSession({ type: 'initialize', auth: { ...parsed, token: parsed.token } });
				} else if (this.isAuthRefreshable(parsed)) {
					this.updateSession({ type: 'refresh', refreshToken: parsed.refreshToken,  })
				} else {
					localStorage.removeItem(this.authKey);
				}
			}
		} catch (e) {
			console.error('Encountered an error initializing auth.');
		}

		// we periodically check if the refresh token has gone
		// bad. if this happens, the user is out of luck and will
		// need to open the magic link again - so this check will
		// allow the UI to update to reflect that
		setInterval(() => {
			if (this.session && !!this.session.authData.auth && !this.isAuthRefreshable(this.session.authData.auth)) {
				this.clearSession();
			}
		}, 10 * 1000);
	}

	isAuthenticated(): boolean {
		if (!this.session) {
			return false;
		} else {
			return !!this.session.authData.auth || this.session.sourceAction.type === 'refresh';
		}
	}
	
	authenticate(id: string, signature: string): Promise<string> {
		return this.updateSession({ type: 'authenticate', id, signature });
	}
	
	getToken(): Promise<string> {
		if (!this.session) {
			return Promise.reject('Authentication data is missing.');
		}
		
		if (this.session.authData.auth) {
			if (this.session.authData.auth.token && !this.isTimestampStale(this.session.authData.auth.expireAt)) {
				return Promise.resolve(this.session.authData.auth.token);
			} else {
				return this.updateSession({ type: 'refresh', refreshToken: this.session.authData.auth.refreshToken, userContextData: this.session.userContextData });
			}
		} else {
			// auth request is still pending
			return this.session.authData.request.then(a => a.token);
		}
	}
	
	getUserContextPromise(): Promise<UserContextSummary> | nundefined {
		return this.session?.userContextData.request;
	}

	getUserContext(): UserContextSummary | nundefined {
		return this.session?.userContextData.context;
	}
	
	getLastEmail(): string | nundefined {
		return this.lastEmail;
	}
 
	clearToken(): void {
		if (this.session && this.session.authData.auth) {
			this.session.authData.auth = { ...this.session.authData.auth, token: null };
			this.onAuthChanged();
		}
	}
	
	signOut(): void {
		this.clearSession();
		
		this.lastEmail = null;
		this.onLastEmailChanged();
	}

	addUserContextChangedListener(cb: UserContextChangedCb): () => void {
		this.userContextChangedCbs.push(cb);
		return () => {
			this.userContextChangedCbs = this.userContextChangedCbs.filter(cb1 => cb1 !== cb);
		}
	}

	addAuthChangedListener(cb: AuthChangedCb): () => void {
		this.authChangedCbs.push(cb);
		return () => {
			this.authChangedCbs = this.authChangedCbs.filter(cb1 => cb1 !== cb);
		}
	}

	addLastEmailChangedListener(cb: LastEmailChangedCb): () => void {
		this.lastEmailChangedCbs.push(cb);
		return () => {
			this.lastEmailChangedCbs = this.lastEmailChangedCbs.filter(cb1 => cb1 !== cb);
		}
	}
	
	// ------------------------------------- //
	
	private updateSession(action: UpdateSessionAuthenticateAction | UpdateSessionRefreshAction | UpdateSessionInitializeAction): Promise<string> {
		const authData: AuthData = action.type === 'initialize'
			?  { request: Promise.resolve(action.auth), auth: action.auth }
			:  this.getAuthData(action);

		const session: Session = {
			sourceAction: action,
			authData,
			userContextData: action.type === 'refresh' && action.userContextData 
				? action.userContextData 
				: this.getUserContextDataForToken(authData.request.then(auth => auth.token))
		};
		
		this.session = session;

		this.onAuthChanged();
		
		this.session.authData.request.then(auth => {
			if (this.session === session) {
				this.onAuthChanged();
				
				localStorage.setItem(this.authKey, JSON.stringify(auth));
			}
		}, () => {
			if (this.session === session) {
				this.clearSession();
			}
		});
		
		this.session.userContextData.request.then(context => {
			if (this.session === session) {
				this.userContextChangedCbs.forEach(cb => cb(context));
				
				this.lastEmail = context.emailAddress;
				this.onLastEmailChanged();
			}
		});
		
		return session.authData.request.then(auth => auth.token);
	}
	
	private getUserContextDataForToken(tokenRequest: Promise<string>): UserContextData {
		const data: UserContextData = {
			request: tokenRequest.then(token => {
				const headers: RawAxiosRequestHeaders = {};
				const config: AxiosRequestConfig = { headers };
				setAuthHeader(headers, token);
				return axios.get<UserContextSummary>(`/api/context`, config).then(r => {
					data.context = r.data;
					return r.data;
				});
			}),
			context: null
		};
		
		return data;
	}
	
	private getAuthData(action: UpdateSessionAuthenticateAction | UpdateSessionRefreshAction): AuthData {
		const requestBody: MagicLinkRequest = action.type === 'refresh'
			? { refreshToken: action.refreshToken }
			: { id: action. id, signature: action.signature };
		
		const data: AuthData = {
			request: axios.post<AuthTokenResponse>('/api/token', requestBody).then(response => {
				if (response.status === 202) {
					const authenticateError: AuthenticateError = { type: 'linkSent' };
					return Promise.reject(authenticateError);
				} else {
					data.auth = response.data;
					return response.data;
				}
			}, (reason) => {
				const authenticateError: AuthenticateError = { type: 'requestError', error: reason };
				return Promise.reject(authenticateError);
			}),
			auth: null
		};
		
		return data;
	}
	
	private isAuthRefreshable(auth: Auth): boolean {
		return !this.isTimestampStale(auth.refreshExpireAt);
	}

	private isTimestampStale(t: string): boolean {
		const target = new Date(t).getTime() - this.clockDiscrepancyBufferMs;
		const now = new Date().getTime();
		return target < now;
	}
	
	private clearSession(): void {
		this.session = null;
		localStorage.removeItem(this.authKey);
		this.onAuthChanged();
	}
	
	private onAuthChanged(): void {
		const isAuthenticated = this.isAuthenticated();
		this.authChangedCbs.forEach(cb => cb(isAuthenticated));
	}
	
	private onLastEmailChanged(): void {
		const lastEmail = this.lastEmail;
		this.lastEmailChangedCbs.forEach(cb => cb(lastEmail));
	}
}

export default new AuthProvider();

interface Session {
	sourceAction: UpdateSessionAuthenticateAction | UpdateSessionRefreshAction | UpdateSessionInitializeAction;
	authData: AuthData;
	userContextData: UserContextData;
}

interface AuthData {
	readonly request: Promise<AuthTokenResponse>;
    auth: Auth | null;
}

/**
 * basically the same as what the server sends
 * back but need token to be nullable so we can
 * clear it out if it is no longer valid.
 */
interface Auth {
	readonly token: string | null;
	readonly expireAt: string;
	readonly refreshToken: string;
	readonly refreshExpireAt: string;
	readonly issuedAt: string;
}

interface UserContextData {
	readonly request: Promise<UserContextSummary>;
	context: UserContextSummary | null;
}

interface UpdateSessionAuthenticateAction {
	type: 'authenticate'; 
	id: string; 
	signature: string;
}

interface UpdateSessionRefreshAction {
	type: 'refresh'; 
	refreshToken: string; 
	userContextData?: UserContextData;
}

interface UpdateSessionInitializeAction {
	type: 'initialize'; 
	auth: AuthTokenResponse;
}

export type AuthenticateError = { type: 'linkSent'; } | { type: 'requestError'; error: AxiosError; };

type AuthChangedCb = (isAuthenticated: boolean) => void;
type UserContextChangedCb = (userContext: UserContextSummary | nundefined) => void;
type LastEmailChangedCb = (email: string | nundefined) => void;
